import { formatISO } from 'date-fns';
import { ClockIcon, LayersIcon, SquareCheckBigIcon } from 'lucide-react';
import { CancellableSearchBox } from '../../../components/search/cancellable-search-box.tsx';
import { PresetDatePicker } from '../../../components/ui/preset-date-picker.tsx';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../components/ui/select.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { BillingStatus, TargetUnit, mapToTargetUnit } from '../../../server/utils/explorer-utils.ts';
import { getSearchStringFieldForTab, useExplorerView } from '../hooks/use-explorer-query-params.ts';
import { MetricInfoHint } from './metric-explainer.tsx';

export function ExplorerFilters() {
    const { t } = useLocale('explorer');

    const { state, setState } = useExplorerView();

    const {
        searchString,
        tab: selectedTab,
        window: selectedWindow,
        customDateRange: selectedCustomDateRange = null,
        billing: selectedBillingStatus,
        sorting: selectedSortingFilter,
        targetUnit: selectedTargetUnit,
        yearPeriods: selectedYearPeriod,
    } = state;

    const setSelectedTargetUnit = (unit: TargetUnit) => {
        setState((prevState) => ({
            ...prevState,
            targetUnit: unit,
            sorting: {
                direction: selectedSortingFilter.direction,
                field: mapToTargetUnit[unit][selectedSortingFilter.field],
            },
        }));
    };
    return (
        // The row scrolls sideways, so it needs an outer box a column flex
        // parent cannot squash — the anchor this replaces used to be that box.
        <div className="flex">
            <div className="flex flex-row gap-2 scrollbar-none overflow-auto">
                <CancellableSearchBox
                    key={selectedTab}
                    placeholder={t('search_placeholder__' + selectedTab)}
                    value={searchString}
                    onChange={(value) => {
                        const tabSearchField = getSearchStringFieldForTab(selectedTab);
                        setState((prevState) => ({
                            ...prevState,
                            [tabSearchField]: value,
                        }));
                    }}
                    className="w-[210px] shrink-0"
                />

                <div className="flex items-center gap-tight">
                    <Select value={selectedTargetUnit} onValueChange={setSelectedTargetUnit}>
                        <SelectTrigger icon={LayersIcon} className="text-left">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                            <SelectItem value={TargetUnit.REVENUE}>
                                {t('filter_dropdown_unit__monetary')}
                            </SelectItem>
                            <SelectItem value={TargetUnit.UNITS}>
                                {t('filter_dropdown_unit__units')}
                            </SelectItem>
                        </SelectContent>
                    </Select>

                    {/* Only Menge needs a hint; what a monetary value counts is the next select's job. */}
                    {selectedTargetUnit === TargetUnit.UNITS && <MetricInfoHint metric="UNITS" />}
                </div>

                <div className="flex items-center gap-tight">
                    <Select
                        value={selectedBillingStatus}
                        onValueChange={(value) => {
                            setState((prevState) => ({
                                ...prevState,
                                billing: value as BillingStatus,
                            }));
                        }}
                    >
                        <SelectTrigger icon={SquareCheckBigIcon} className="h-8 text-left">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                            <SelectItem key={BillingStatus.BILLED} value={BillingStatus.BILLED}>
                                {t('filter_dropdown_status__billed')}
                            </SelectItem>
                            <SelectItem
                                key={BillingStatus.BILLED_WO_CREDIT_NOTE}
                                value={BillingStatus.BILLED_WO_CREDIT_NOTE}
                            >
                                {t('filter_dropdown_status__billed_wo_credit_note')}
                            </SelectItem>
                            <SelectItem key={BillingStatus.NOT_BILLED} value={BillingStatus.NOT_BILLED}>
                                {t('filter_dropdown_status__unbilled')}
                            </SelectItem>
                            <SelectItem key={BillingStatus.ORDER_SUM} value={BillingStatus.ORDER_SUM}>
                                {t('filter_dropdown_status__order_sum')}
                            </SelectItem>
                        </SelectContent>
                    </Select>

                    <MetricInfoHint metric={selectedBillingStatus} />
                </div>

                <div className="flex shrink-0">
                    <PresetDatePicker
                        value={{
                            preset: selectedWindow,
                            dateRange: selectedCustomDateRange
                                ? {
                                      from: new Date(selectedCustomDateRange.from),
                                      to: new Date(selectedCustomDateRange.to),
                                  }
                                : null,
                        }}
                        onChange={({ preset, dateRange }) => {
                            setState((prevState) => ({
                                ...prevState,
                                window: preset,
                                customDateRange: dateRange
                                    ? {
                                          from: formatISO(dateRange.from, { representation: 'date' }),
                                          to: formatISO(dateRange.to, { representation: 'date' }),
                                      }
                                    : undefined,
                            }));
                        }}
                    />
                </div>

                <div className="flex">
                    <Select
                        value={selectedYearPeriod.toString()}
                        onValueChange={(value) => {
                            setState((prevState) => ({
                                ...prevState,
                                yearPeriods: value ? Number.parseInt(value, 10) : 2,
                            }));
                        }}
                    >
                        <SelectTrigger icon={ClockIcon} className=" text-left">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                            {[2, 3, 4, 5].map((years) => (
                                <SelectItem key={years} value={years.toString()}>
                                    {t('filter_dropdown__period', { years })}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>
        </div>
    );
}
